<script lang="ts" setup>
import IndividualPrice from '../../../IndividualPrice/IndividualPrice.vue';
import type { PriceItem as Item } from '../../../../types/priceItem';
import { computed, ref } from 'vue';
import { watch } from 'vue';
import { useStoreTranslations } from '../../../../store/translations/translations';
import Loading from '../../../Loading/Loading.vue';
import FormNumber from '../../../FormNumber/FormNumber.vue';

type Props = {
  item: Item;
  selectOptions: string[];
  value: number;
  isLoading: boolean;
  isReadOnly?: boolean;
};

const props = defineProps<Props>();
const emit = defineEmits(['itemQuantityChange', 'updatedPrice', 'removeItem', 'amountWarning']);

const itemQuantity = ref(props.item.quantity);
const itemPrice = ref(props.item.pricesingle);

const storeTranslations = useStoreTranslations();

const translateStrings = (category: string, key: string): string => {
  return storeTranslations.translations?.[`${category}.${key}`] ?? '';
};

const helperText = computed(() => {
  const highestQty = props.item.highestQty;
  const translatedText = translateStrings('shopCartItem', 'helperText');

  if (!highestQty || !translatedText) {
    return '';
  }

  return translatedText.replace(/25/g, String(highestQty));
});

const articleNumberText = (id: string): string => {
  return !id.includes('none_')
    ? id
    : (storeTranslations.translations?.['shopCart.noArtNoAvailableText'] ?? '');
};

watch(itemQuantity, (newValue, oldValue) => {
  const newValueNumber = Number(newValue);
  const oldValueNumber = Number(oldValue);
  const itemRaw = props.item;

  if (newValueNumber !== oldValueNumber) {
    emit('itemQuantityChange', { quantity: newValueNumber, item: itemRaw });
  }
});
</script>

<template>
  <article :key="item.id" :class="$style.container" role="listitem" :title="item.title">
    <div v-if="isLoading" :class="$style.isLoading">
      <Loading />
    </div>
    <button
      v-if="!isReadOnly"
      :class="$style.deleteBtn"
      :name="translateStrings('shopCartItem', 'deleteItem') || 'Delete Item'"
      :title="translateStrings('shopCartItem', 'deleteItem') || 'Delete Item'"
      @click="() => emit('removeItem', { item: item, id: item.id })"
    />
    <img :alt="item.name" :src="item.thumbnail" />
    <div :class="$style.productInfoBox">
      <h3 v-if="!isReadOnly" v-html="item.title" />
      <p v-else :class="$style.titleReadOnly" v-html="item.title" />
      <p :class="isReadOnly ? $style.name : null">{{ item.name }}</p>
      <dl>
        <dt>{{ translateStrings('shopCartItem', 'articleNoLabel') || 'Article Nr.' }}:</dt>
        <dd>{{ articleNumberText(item.id) }}</dd>
        <dt>{{ translateStrings('shopCartItem', 'articlePrice') || 'Price (per unit)' }}:</dt>
        <dd>
          <IndividualPrice
            :product-type="item.productType"
            :quantity="1"
            :value="item.pricesingle"
          />
        </dd>
      </dl>
      <div v-if="!isReadOnly" :class="$style.productPriceBox">
        <FormNumber
          :decrease-label="translateStrings('numberInput', 'decrease')"
          :error-message="
            translateStrings('shopCartItem', 'errorMessage') || 'an error is occurred'
          "
          :helper-text="helperText"
          :increase-label="translateStrings('numberInput', 'increase')"
          :initial-value="item.quantity"
          :label="translateStrings('shopCartPopper', 'quantity') || 'item quantity'"
          :max-message="translateStrings('numberInput', 'maxMessage')"
          :max-value="item.highestQty"
          :min-message="translateStrings('numberInput', 'minMessage')"
          :min-value="item.lowestQty || 1"
          :optional="false"
          :step="item.multipleQty || 1"
          @on-change="(newQuantity) => (itemQuantity = Number(newQuantity))"
          @rule-applied="(rule) => emit('amountWarning', { rule, item })"
        />
        <IndividualPrice
          :product-type="item.productType"
          :quantity="itemQuantity"
          :value="itemPrice"
          @on-change="(newPrice) => emit('updatedPrice', newPrice)"
        />
      </div>
    </div>
  </article>
</template>

<style lang="scss" module>
@forward 'ShopCartItem.scss';
</style>
